import { ApolloError, gql, useMutation } from '@apollo/client';
import {
  Button,
  Card,
  CardBody,
  CardDescription,
  CardHeader,
  CardHeaderControl,
  CardTitle,
} from '@apollo/orbit';
import { useFormik } from 'formik';
import React, { useCallback, useState } from 'react';
import { useLocation } from 'react-router';
import { Link } from 'react-router-dom';
import * as Yup from 'yup';

import { URLModal } from 'src/components/common/URLModal';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { PermissionWarning } from 'src/components/permissionGuards/permissionWarning/PermissionWarning';
import Config from 'src/lib/config';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { setOverlayVisibleLocation } from 'src/lib/routing';

import { ConfigureExcludedClientsModal } from '../../checksConfigPage/configureExcludedClientsModal/ConfigureExcludedClientsModal';
import {
  STATS_TIME_RANGE,
  checksTimeRangeInSeconds,
} from '../../checksConfigPage/constants';
import { ExcludedClientsTable } from '../../checksConfigPage/excludedClientsTable/ExcludedClientsTable';
import { removeTypename } from '../../checksConfigPage/transforms';
import { SharedCheckConfigCardProps } from '../common/SharedCheckConfigCardProps';
import { UseGraphSettingsButton } from '../common/UseGraphSettingsButton';
import { VariantCheckConfigFragment } from '../VariantCheckConfigFragment';

const variantCheckConfigExcludedClientsMutation = gql`
  mutation VariantCheckConfigExcludedClientsMutation(
    $graphId: ID!
    $graphVariant: String!
    $appendGraphSettings: Boolean!
    $excludedClients: [ClientFilterInput!]
  ) {
    service(id: $graphId) {
      variant(name: $graphVariant) {
        updateCheckConfigurationExcludedClients(
          appendGraphSettings: $appendGraphSettings
          excludedClients: $excludedClients
        ) {
          id
          ...VariantCheckConfigFragment
        }
      }
    }
  }
  ${VariantCheckConfigFragment}
`;

/**
 * Schema for the form
 */
const validationSchema = Yup.object().shape({
  appendGraphSettings: Yup.boolean().required(),
  excludedClients: Yup.array(
    Yup.object({
      name: Yup.string(),
      version: Yup.string().nullable(),
    }),
  ).nullable(),
});

/**
 * Types inferred from `validationSchema`
 */
type FormValues = Yup.InferType<typeof validationSchema>;

type Props = SharedCheckConfigCardProps & {
  canViewClientVersions: boolean;
};

export const ExcludedClientsCard = ({
  graphRef,
  graphCheckConfig,
  variantCheckConfig,
  canViewClientVersions,
  roles,
}: Props) => {
  const location = useLocation();

  const [updateConfigError, setUpdateConfigError] =
    useState<ApolloError | null>(null);

  const [updateCheckConfig] = useMutation<
    GraphQLTypes.VariantCheckConfigExcludedClientsMutation,
    GraphQLTypes.VariantCheckConfigExcludedClientsMutationVariables
  >(variantCheckConfigExcludedClientsMutation, {
    onError: setUpdateConfigError,
  });

  const appendGraphSettings =
    variantCheckConfig.excludedClientsConfig.appendGraphSettings;

  const { values, setFieldValue, submitForm } = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: {
      appendGraphSettings,
      excludedClients: removeTypename(
        variantCheckConfig.excludedClientsConfig.excludedClients,
      ),
    },
    onSubmit: async (formValues) => {
      await updateCheckConfig({
        variables: {
          graphId: graphRef.graphId,
          graphVariant: graphRef.graphVariant,
          appendGraphSettings: formValues.appendGraphSettings,
          excludedClients: formValues.excludedClients,
        },
      });
    },
  });

  const clearErrorsAndSubmit = useCallback(() => {
    setUpdateConfigError(null);
    submitForm();
  }, [setUpdateConfigError, submitForm]);

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Excluded clients</CardTitle>
          <CardDescription>
            Any operations sent by these clients will not be checked against
            changes to the schema. This is useful to filter out clients that are
            used to run ad hoc operations.
          </CardDescription>

          <CardHeaderControl>
            <UseGraphSettingsButton
              label="Append Graph Settings"
              tooltipContent={
                values.appendGraphSettings
                  ? 'Additionally exclude clients configured by this graph.'
                  : undefined
              }
              isSelected={values.appendGraphSettings}
              disabled={!roles.canWriteCheckConfiguration}
              onChange={(value) => {
                setFieldValue('appendGraphSettings', value);
                clearErrorsAndSubmit();
              }}
            />
            <PermissionWarning
              graphId={graphRef.graphId}
              graphPermissions="canWriteCheckConfiguration"
              childRenderer={(hasPermission) => (
                <Button
                  type="button"
                  as={Link}
                  to={setOverlayVisibleLocation({
                    location,
                    overlayName: Config.modals.configureExcludedClients,
                  })}
                  isDisabled={!hasPermission}
                >
                  Configure
                </Button>
              )}
            />
          </CardHeaderControl>
        </CardHeader>
        <CardBody>
          <ExcludedClientsTable
            canViewVersions={canViewClientVersions}
            graphId={graphRef.graphId}
            excludedClients={values.excludedClients}
            appendedExcludedClients={
              values.appendGraphSettings
                ? graphCheckConfig.excludedClients
                : undefined
            }
            onExcludedClientRemove={(clientName: string) => {
              setFieldValue(
                'excludedClients',
                values.excludedClients?.filter(
                  (client) => client.name !== clientName,
                ),
              );
              clearErrorsAndSubmit();
            }}
            showActions={true}
          />

          {/* Form update error message */}
          {updateConfigError && <ErrorMessage error={updateConfigError} />}
        </CardBody>
      </Card>

      {/* Popup modal */}
      <URLModal name={Config.modals.configureExcludedClients}>
        {({ isOpen, closeModal }) => (
          <ConfigureExcludedClientsModal
            isOpen={isOpen}
            graphId={graphRef.graphId}
            showClientsFromTime={`-${checksTimeRangeInSeconds[STATS_TIME_RANGE]}`}
            excludedClients={[
              ...(values.excludedClients ?? []),
              ...(values.appendGraphSettings
                ? graphCheckConfig.excludedClients
                : []),
            ]}
            onUpdate={(excludedClients) => {
              setFieldValue('excludedClients', excludedClients);
              clearErrorsAndSubmit();
              closeModal();
            }}
            onClose={closeModal}
          />
        )}
      </URLModal>
    </>
  );
};
